import React, { FC, memo, useEffect, useMemo, useRef } from "react";
import Image from "next/image";
import SwiperCore, { Navigation, A11y, Zoom } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { Item } from "../../../graphql/generated";
import { getCloudflareLoader } from "@pepdirect/helpers/cloudflareImages";
import {
  StyledAspectRatio,
  StyledAspectRatioWrapper,
  StyledNote,
  StyledWrapper,
} from "./index.styled";

import getConfig from "next/config";
const { publicRuntimeConfig } = getConfig();

SwiperCore.use([Navigation, A11y, Zoom]);

interface PDPImagesProps {
  image: string;
  images: Item["imgVariantUrls"];
  alt?: string;
}

const Images: FC<PDPImagesProps> = ({ image, images = [], alt = "" }) => {
  const swiperRef = useRef<SwiperCore | null>(null);
  const options: Swiper = {
    passiveListeners: false,
    slidesPerView: 1,
    navigation: {
      nextEl: "#images-next-slide",
      prevEl: "#images-prev-slide",
    },
    zoom: true,
    onSwiper: (swiper) => (swiperRef.current = swiper),
  };

  const allImages = useMemo(
    () => [
      image,
      ...(images
        ? images
            .slice()
            .sort((a, b) => (a?.pos || 0) - (b?.pos || 0))
            .map((item) => item?.src)
        : []),
    ],
    [images, image]
  );

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.update();
    }
  });

  return (
    <div>
      <StyledWrapper>
        <div className="swiper-button-custom--prev" id="images-prev-slide" />
        <Swiper {...options} style={{ width: "100%" }}>
          {allImages.map((src) => (
            <SwiperSlide key={src} style={{ width: "100%" }}>
              <StyledAspectRatioWrapper>
                <StyledAspectRatio className="swiper-zoom-container">
                  <Image
                    src={src || ""}
                    fill
                    // allows for large sizes to accommodate zoom
                    sizes="auto"
                    loader={getCloudflareLoader(
                      publicRuntimeConfig.productsImgCdn
                    )}
                    alt={alt}
                    priority
                  />
                </StyledAspectRatio>
              </StyledAspectRatioWrapper>
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="swiper-button-custom--next" id="images-next-slide" />
      </StyledWrapper>
      <StyledNote>Double-tap or pinch image to zoom.</StyledNote>
    </div>
  );
};

export const PDPImages = memo(Images);
